import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { createSelectedVehicleSelectionSelector } from 'Core/selectors/fitmentSearch/index.js';
import { SearchItemDataCollection } from 'Models/index.ts';
import productConfig from 'Models/uiConfig/productConfig.js';
import requestConfig from 'Models/uiConfig/requestConfig';
import { search } from 'Modules/serverApi/index.js';

import type { FC } from 'react';
import type { RepeaterFunctionInvoker, TemplateFunction } from 'Components/types.ts';
import type { SearchItemData } from 'Models/index.ts';

type ItemData = SearchItemData & {
  isSelected: boolean;
  selectItem: () => void;
};

type Params = {
  items: RepeaterFunctionInvoker<ItemData>;
  totalPrice: string;
  addItemsToCart: () => void;
};

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  mode: string;
  count?: number;
};

const { localItemId, formatPrice } = productConfig;
const { addItemsToCart } = requestConfig;

const BuyItemsTogether: FC<Props> = ({ template, mode, count }) => {
  const vehicleSelection = useSelector(createSelectedVehicleSelectionSelector);

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [items, setItems] = useState<SearchItemDataCollection>();
  const [totalPrice, setTotalPrice] = useState<number>(0);

  useEffect(() => {
    if (!count) {
      return;
    }

    search({
      selection: [...vehicleSelection],
      pageSize: count,
      extra: { itemId: localItemId, mode },
    }).then((response) => {
      const responseItems = response.Items;

      if (responseItems.length > 0) {
        const itemCollection = new SearchItemDataCollection(responseItems, 'boughtTogether');
        const ids = itemCollection.map((item: SearchItemData) => item.entityId);

        setSelectedItemIds(ids);
        setItems(itemCollection);

        setTotalPrice(
          itemCollection
            .map((item: SearchItemData) => item.price ?? 0)
            .reduce((total, price) => total + price, 0),
        );
      }
    });
  }, [count, mode, vehicleSelection]);

  if (!items?.length) {
    return;
  }

  const handleItemSelection = (itemId: string) => {
    const isSelected = selectedItemIds.includes(itemId);
    setSelectedItemIds(
      isSelected ? selectedItemIds.filter((id) => id !== itemId) : [...selectedItemIds, itemId],
    );

    const itemPrice = items.find((item: SearchItemData) => item.entityId === itemId)?.price as number;
    setTotalPrice(isSelected ? totalPrice - itemPrice : totalPrice + itemPrice);
  };

  function itemComponents({ template, item }) {
    return template.call({
      ...item.params,
      isSelected: selectedItemIds.includes(item.entityId),
      selectItem: () => handleItemSelection(item.entityId),
    });
  }

  return template.call({
    items: items.repeaterComponents(itemComponents) as RepeaterFunctionInvoker<ItemData>,
    totalPrice: formatPrice(totalPrice),
    addItemsToCart: () => addItemsToCart(selectedItemIds),
  });
};

export default BuyItemsTogether;
